import { datadogRum } from "@datadog/browser-rum";
import { EMKAY_PARTNER_CODES } from "../app-consts/appConstants";
import { determineLocale } from "../contexts/LocaleContext";

interface RescueTrackerCallbacks {
  rescueTrackerCallback?: string;
  spanishRescueTrackerCallback?: string;
  frenchRescueTrackerCallback?: string;
}

/**
 * Resolves the rescue tracker callback number for the current locale.
 *
 * Emkay partners (ERU, EMS, ERC, ESC) maintain dedicated Spanish and French
 * callback numbers; every other partner always uses the default callback.
 */
export const getRescueTrackerCallbackPhone = (
  displayCode: string | undefined,
  callbacks: RescueTrackerCallbacks = {}
): string | null | undefined => {
  const {
    rescueTrackerCallback,
    spanishRescueTrackerCallback,
    frenchRescueTrackerCallback,
  } = callbacks;

  const isEmkayPartner = EMKAY_PARTNER_CODES.includes(displayCode);
  const locale = determineLocale().toLowerCase();

    if (isEmkayPartner && locale.startsWith("es_")) {
      return spanishRescueTrackerCallback;
    }
    if (isEmkayPartner && locale.startsWith("fr_")) {
      return frenchRescueTrackerCallback;
    }
    return rescueTrackerCallback;
  };

/**
 * Sanitizes a phone number value before it is used in security-sensitive contexts
 * (e.g. window.location.href = "tel:" + phone) to prevent Open Redirect vulnerabilities
 * from unsanitized input (such as values read from localStorage).
 *
 * Only allows characters that are valid in a tel: URI: digits, spaces, +, -, (, ), .
 * Returns an empty string if the value is not a string or does not match the allowed pattern.
 */
export const sanitizePhoneForTelLink = (phone: string | null | undefined): string => {
  return typeof phone === "string" && phone.match(/^[0-9+()\-.\s]+$/)
    ? phone.trim()
    : "";
};

/**
 * Resolves the customer service phone number to call, falling back to the
 * toll free number stored in localStorage (set from the benefits lookup response)
 * when no partner phone is available.
 */
export const getCustomerServicePhone = (partnerPhone?: string | null): string | null => {
  return partnerPhone || localStorage.getItem("tollFreeNumber");
};

/**
 * Sanitizes an already-resolved phone number and dials it via a tel: link.
 * Logs a datadog error tagged with the calling component's name when no valid
 * phone number could be resolved, matching the previous per-component behavior.
 */
export const dialCustomerServicePhone = (
  phone: string | null | undefined,
  componentName: string
): void => {
  // Sanitize before using in window.location to prevent Open Redirect
  const sanitizedPhone = sanitizePhoneForTelLink(phone);
  if (sanitizedPhone) {
    window.location.href = "tel:" + sanitizedPhone;
  } else {
    datadogRum.addError(`[${componentName}] Unable to get customer service number for user to call`);
  }
};

/**
 * Resolves the customer service phone number (falling back to localStorage's
 * tollFreeNumber when needed), sanitizes it, and dials it via a tel: link.
 */
export const callCustomerServicePhone = (
  partnerPhone: string | null | undefined,
  componentName: string
): void => {
  dialCustomerServicePhone(getCustomerServicePhone(partnerPhone), componentName);
};

